import React, { useEffect } from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { Helmet } from "react-helmet";
import {
  LinkedinShareButton,
  LinkedinIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
  RedditShareButton,
  RedditIcon,
} from 'react-share';

const CriminalCaseGuide = () => {
  const shareUrl = 'https://legalapp.in/blogs/criminal-case-guide';
  const title = 'Criminal Case Proceedings in India: What to Expect – A Guide for Accused and Complainants';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      backgroundColor: "white",
      color: "black",
      fontFamily: "Arial, sans-serif"
    }}>
      <Helmet>
        <title>LegalApp</title>
        <meta
          name="description"
          content="Know your rights and the criminal process in India. This guide simplifies FIRs, investigations, trials, and appeals for everyday understanding."
        />
      </Helmet>
      <Navbar />
      <main style={{
        flex: "1",
        padding: "20px",
        width: "100%",
        boxSizing: "border-box",
        marginTop: "40px",
      }}>
        <div style={{ maxWidth: '900px', margin: 'auto', padding: '20px' }}>
          <h2
            style={{
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '15px',
            }}
          >
            Criminal Case Proceedings in India: What to Expect <br></br> A Guide for Accused and Complainants
          </h2>

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', alignContent: 'center', justifyContent: 'center' }}>
            <LinkedinShareButton url={shareUrl} title={title}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>
            <TwitterShareButton url={shareUrl} title={title}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={title}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <RedditShareButton url={shareUrl} title={title}>
              <RedditIcon size={32} round />
            </RedditShareButton>
          </div>

          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            Whether you're accused in a case or the person filing the complaint, India's criminal justice process can feel confusing and intimidating. But understanding each step can help you feel confident, know your rights, and navigate the process with clarity.
          </p>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            Let’s go step-by-step and explain what happens in each phase of a criminal case, and what both parties should expect.
          </p>
          <br></br>

          <h3 style={{ marginTop: '-10px' }}>1. Registration of FIR (First Information Report)</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            When a complainant reports a cognizable offence (like theft, assault, or dowry harassment), the police register an FIR under Section 154 CrPC/173 BNSS. This formally begins the case.
          </p>

          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Be clear and truthful about what happened.</li>
            <li>Share all facts, dates, times, and evidence.</li>
            <li>Vague or exaggerated claims can hurt the case later.</li>
          </ul>


          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>FIR <strong>does NOT mean guilt</strong>. It's just an accusation to be investigated.</li>
            <li>You're presumed innocent until the court says otherwise.</li>
            <li>Do not panic. Consult a lawyer and understand your rights.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-5px' }}>2. Investigation</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            Police investigate the complaint—this includes visiting the crime scene, collecting evidence, questioning both parties, and recording statements under Section 161 CrPC/180 BNSS.
          </p>

          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Share documents, witness names, messages, photos, or anything that supports your version.</li>
            <li>Incomplete or inconsistent information weakens the case.</li>
          </ul>


          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>You may be questioned, but you’re <strong>not obligated to confess.</strong></li>
            <li>Police cannot force a confession.</li>
            <li>Vague or unverified allegations can create reasonable doubt in your favor later.</li>
          </ul>
          <p>🛡️ Note: You can apply for anticipatory bail if you're worried about being arrested.</p>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>3. Anticipatory Bail</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            If you (accused) fear arrest, you can seek anticipatory bail under Section 438 CrPC/482 BNSS. If granted, you cannot be arrested immediately.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>You may be notified about the bail hearing and can oppose it with your lawyer.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>File anticipatory bail in Sessions Court or High Court.</li>
            <li>The court may ask for your cooperation with the investigation.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>4. Filing of Chargesheet or Closure Report</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            After investigation, police either file a chargesheet (if evidence supports prosecution) or a closure report (if it doesn’t) under Section 173 CrPC/193 BNSS.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>If police file a closure report, you can challenge it by filing a protest petition in court.</li>
            <li>Work closely with your lawyer to ensure strong submissions.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Chargesheet will list the specific offences you're being charged with.</li>
            <li>Analyse it carefully with your lawyer—it sets the tone for the entire trial.</li>
          </ul>

          <br></br>


          <h3 style={{ marginTop: '-1px' }}>5. Cognizance by Magistrate</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            The Magistrate examines the police report and decides whether to proceed with the case.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>This is a crucial step—court formally begins the legal case.</li>
            <li>Ensure your FIR and evidence are clear and consistent.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>If there's a weak case, your lawyer can argue for dismissal or discharge.</li>
            <li>No need to panic—the FIR may not result in conviction if the supporting evidence is missing.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>6. Summoning the Accused</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            The court issues summons or a warrant requiring the accused to appear before the judge.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>This shows the court is moving forward. You may be asked to be present in future hearings.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Comply with the summons. Cooperating with the court builds credibility.</li>
            <li>You may apply for regular bail now if not already granted.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>7. Bail of Accused</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            The accused can apply for regular bail under Section 437 CrPC/480 BNSS or Section 439 CrPC/483 BNSS.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>You can oppose bail if you believe the accused may threaten you or tamper with evidence.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Bail is a legal right, except in non-bailable offenses where it is granted at the court's discretion.</li>
            <li>Follow bail conditions carefully to avoid cancellation.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>8. Discharge Application (Before Trial Begins)</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            Before framing charges, the accused can request to be discharged if the charges are baseless.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Your lawyer must demonstrate to the court that a prima facie case exists.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Argue that the case lacks sufficient evidence to go to trial.</li>
            <li>If successful, you’re released without facing trial.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>9. Framing of Charges</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            The court frames the formal charges against the accused, defining what offences will be tried.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Be prepared for the next stage—<strong>presenting evidence</strong> to prove your case.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>This is when the trial officially begins.</li>
            <li>You’ll plead guilty or not guilty.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>10. Prosecution Evidence & Witness Examination</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            The prosecution presents its case—documents, witnesses, and other evidence.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Attend all hearings. Your testimony is crucial.</li>
            <li>Avoid exaggerations or contradictions during cross-examination.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Look for gaps in the prosecution’s story.</li>
            <li>Your lawyer will cross-examine witnesses to expose inconsistencies or falsehoods.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>11. Statement of Accused (Section 313 CrPC/351 BNSS)</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            The judge asks the accused to explain the circumstances and respond to the evidence.
          </p>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>You are not under oath. This is your chance to explain your side.</li>
            <li>You can remain silent or deny allegations.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>12. Defense Evidence (Optional)</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            The accused may present documents or witnesses—but <strong>not mandatory</strong>.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Stay attentive. Your lawyer can cross-examine defense witnesses to maintain the strength of your case.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Use this stage only if it strengthens your defense.</li>
            <li>Witnesses should be credible and consistent.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>13. Final Arguments</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            Both sides summarize their case and try to convince the judge.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Your lawyer will highlight strong evidence and witness credibility.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Your lawyer will point out contradictions, missing links, or weak evidence.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>14. Judgment</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            The judge delivers the verdict: conviction or acquittal.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>If acquitted, you can appeal.</li>
            <li>If convicted, sentencing is the next step.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>If acquitted, you're free. If convicted, sentencing will follow.</li>
            <li>You can appeal the judgment.</li>
          </ul>

          <br></br>

          <h3 style={{ marginTop: '-1px' }}>15. Sentencing</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            📌 <strong>What it is:</strong>
            <br></br>
            If convicted, the judge decides the punishment—jail, fine, or both.
          </p>
          <p>👤 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>You may give a victim impact statement or request a strict sentence.</li>
          </ul>

          <p>👤 <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>You may argue for a lighter sentence based on circumstances.</li>
          </ul>

          <br></br>

          <h3>🧾 What Happens After Judgment?</h3>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', lineHeight: "1.4" }}>
            <li><strong>Appeal:</strong> Either party can approach a higher court.</li>
            <li><strong>Revision:</strong> For correcting legal or procedural errors.</li>
            <li><strong>Writ Petition:</strong> For rights violation.</li>
            <li><strong>SLP:</strong> Special permission to challenge lower court decisions before Supreme Court.</li>
            <li><strong>Sec 482 CrPC/528 BNSS:</strong> For quashing or stopping abuse of process in High Court.</li>
          </ul>



          <h3 style={{ marginTop: '40px' }}>✅ Key Takeaways for Both Sides</h3>
          <p>📣 <strong>For the Complainant:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>Allegations must be backed by <strong>evidence and witnesses.</strong></li>
            <li>Your cooperation with the investigation is critical.</li>
            <li>Inconsistencies can lead to acquittal. Be <strong>clear, complete, and truthful</strong>.</li>
          </ul>

          <p>✋ <strong>For the Accused:</strong></p>
          <ul style={{ paddingLeft: '35px', fontSize: '17px', marginTop: '-10px', lineHeight: "1.4" }}>
            <li>FIR is <strong>not proof of guilt</strong>.</li>
            <li>You are <strong>innocent until proven guilty</strong>.</li>
            <li>You have the right to <strong>bail, silence, and fair trial</strong>.</li>
            <li><strong>Reasonable doubt = acquittal</strong>. Weak prosecution cases often fail.</li>
          </ul>


          <h3 style={{ marginTop: '40px' }}>📘 Conclusion</h3>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            India’s criminal justice system is designed to protect truth and fairness. Whether you're seeking justice or defending yourself, understanding the process is your first step to empowerment.
          </p>
          <p style={{ textAlign: 'justify', fontSize: "18px", lineHeight: "1.4" }}>
            Stay calm, get legal advice, and trust the process.
          </p>

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', alignContent: 'right', justifyContent: 'right' }}>
            <LinkedinShareButton url={shareUrl} title={title}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>
            <TwitterShareButton url={shareUrl} title={title}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={title}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <RedditShareButton url={shareUrl} title={title}>
              <RedditIcon size={32} round />
            </RedditShareButton>
          </div>

          <br></br><br></br><br></br>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CriminalCaseGuide;
